import { z } from 'zod';
export const assetTypeSchema = z.enum([
    'IMAGE',
    'VIDEO',
    'VOICE',
    'MUSIC',
    'AUDIO',
    'STORYBOARD',
    'SCRIPT',
    'RENDER',
]);
export const assetStatusSchema = z.enum(['AVAILABLE', 'ARCHIVED']);
export const imageCategorySchema = z.enum([
    'characters',
    'locations',
    'products',
    'scenes',
    'misc',
]);
export const projectAssetUsageSchema = z.enum([
    'LIBRARY',
    'SCENE_VISUAL',
    'SCENE_VOICE',
    'PROJECT_MUSIC',
    'RENDER_OUTPUT',
]);
/** Tipos admitidos como visual de una escena (docs/03_DATABASE.md). */
export const VISUAL_ASSET_TYPES = ['IMAGE', 'VIDEO'];
/** Tipos admitidos como voz de escena. */
export const VOICE_ASSET_TYPES = ['VOICE', 'AUDIO'];
/** Tipos admitidos como música del proyecto. */
export const MUSIC_ASSET_TYPES = ['MUSIC', 'AUDIO'];
/** Qué tipos puede ocupar cada rol. El selector de la UI usa esta misma tabla. */
export const USAGE_ALLOWED_TYPES = {
    SCENE_VISUAL: VISUAL_ASSET_TYPES,
    SCENE_VOICE: VOICE_ASSET_TYPES,
    PROJECT_MUSIC: MUSIC_ASSET_TYPES,
    RENDER_OUTPUT: ['RENDER'],
    LIBRARY: assetTypeSchema.options,
};
export const ASSET_TAG_MAX_LENGTH = 60;
export const ASSET_TAGS_MAX_COUNT = 20;
/** Las etiquetas se normalizan: minúsculas, sin espacios dobles, sin repetir. */
export const assetTagsSchema = z
    .array(z.string().trim().min(1).max(ASSET_TAG_MAX_LENGTH))
    .max(ASSET_TAGS_MAX_COUNT)
    .transform((tags) => [
    ...new Set(tags.map((tag) => tag.toLowerCase().replace(/\s+/g, ' ').trim())),
]);
/** Metadata técnica. Se rellena desde FFprobe o el decodificador de imagen. */
export const assetMetadataSchema = z.object({
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    durationMs: z.number().int().nonnegative().optional(),
    videoCodec: z.string().optional(),
    audioCodec: z.string().optional(),
    sampleRate: z.number().int().positive().optional(),
    channels: z.number().int().positive().optional(),
    bitrate: z.number().int().positive().optional(),
});
/**
 * DTO de asset. `sizeBytes` viaja como string porque un BigInt no sobrevive a
 * JSON.stringify y un número podría perder precisión por encima de 2^53.
 * Nunca incluye `storageKey`: revelaría el layout interno del servidor.
 */
export const assetSchema = z.object({
    id: z.uuid(),
    type: assetTypeSchema,
    status: assetStatusSchema,
    category: imageCategorySchema.nullable(),
    filename: z.string(),
    mimeType: z.string(),
    sizeBytes: z.string(),
    checksumSha256: z.string(),
    metadata: assetMetadataSchema,
    tags: z.array(z.string()),
    originProjectId: z.uuid(),
    generationId: z.uuid().nullable(),
    sourceAssetId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    /** Ruta protegida de descarga, por ID. Nunca una ruta de filesystem. */
    contentUrl: z.string(),
});
export const assetListQuerySchema = z.object({
    projectId: z.uuid().optional(),
    originProjectId: z.uuid().optional(),
    type: assetTypeSchema.optional(),
    category: imageCategorySchema.optional(),
    status: assetStatusSchema.optional(),
    /** Texto libre sobre filename, categoría y etiquetas. */
    q: z.string().trim().max(200).optional(),
    tag: z.string().trim().max(ASSET_TAG_MAX_LENGTH).optional(),
    createdAfter: z.iso.datetime().optional(),
    createdBefore: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    cursor: z.uuid().optional(),
});
export const associateAssetRequestSchema = z.object({
    assetId: z.uuid(),
    usage: projectAssetUsageSchema.default('LIBRARY'),
});
export const updateAssetRequestSchema = z
    .object({
    filename: z.string().trim().min(1).max(150).optional(),
    category: imageCategorySchema.optional(),
    tags: assetTagsSchema.optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'No hay nada que actualizar.' });
/** Asignación de un asset a un hueco de escena o a la música del proyecto. */
export const assignAssetRequestSchema = z.object({
    expectedRevision: z.number().int().min(1),
    assetId: z.uuid().nullable(),
});
export function isTypeAllowedForUsage(type, usage) {
    return USAGE_ALLOWED_TYPES[usage].includes(type);
}
//# sourceMappingURL=assets.js.map